import { useState, useEffect, useCallback } from "react";
import { Save, CheckCircle2, Lock, Eye, EyeOff, BookOpen, Wrench, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, API_BASE_URL } from "./adminApi";

const SECTIONS = [
  {
    title: "Brevo SMS",
    description: "Service d'envoi de SMS opérationnels et marketing. Récupérez votre clé sur app.brevo.com/settings/keys/api",
    fields: [
      { key: "BREVO_API_KEY", label: "Clé API Brevo", placeholder: "xkeysib-...", secret: true, help: "Clé secrète Brevo. Stockée chiffrée en base." },
      { key: "BREVO_SENDER_ID", label: "Sender ID (nom expéditeur SMS)", placeholder: "FR INTERIM", help: "Doit être validé dans Brevo → SMS settings → Senders." },
    ],
  },
  {
    title: "URL publique",
    description: "Utilisée pour construire les liens dans les emails (bouton 'Connectez-vous à votre espace').",
    fields: [
      { key: "PUBLIC_FRONTEND_URL", label: "URL publique du site", placeholder: "https://fr-interim.fr", help: "Sans / final." },
    ],
  },
  {
    title: "Anael TT (outil métier)",
    description: "Synchronisation des salariés vers le webservice Anael TT V9R4. Tant que l'URL est vide, le mode reste 'simulated'.",
    fields: [
      { key: "ANAEL_BASE_URL", label: "URL de base", placeholder: "https://anael.example.com/V1", help: "URL complète avec /V1 mais sans /candidat." },
      { key: "ANAEL_AUTH_TYPE", label: "Type d'authentification", select: ["none", "basic", "bearer"] },
      { key: "ANAEL_USERNAME", label: "Username (si Basic Auth)", placeholder: "Laisser vide si Bearer/None" },
      { key: "ANAEL_PASSWORD", label: "Password (si Basic Auth)", placeholder: "...", secret: true },
      { key: "ANAEL_API_TOKEN", label: "Token API (si Bearer)", placeholder: "...", secret: true },
      { key: "ANAEL_SOC_CODE", label: "Code société (PERSOC)", placeholder: "001" },
      { key: "ANAEL_DEFAULT_INDICE", label: "Indice par défaut (PERIND)", placeholder: "PRA" },
    ],
  },
];

function FieldRow({ field, value, onSave, saving }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");

  useEffect(() => { if (!editing) setLocalValue(""); }, [editing]);

  const isSecret = !!field.secret;
  const isSet = value?.is_set;
  const displayValue = value?.value;

  const handleSave = async () => {
    await onSave(field.key, localValue);
    setEditing(false);
    setLocalValue("");
  };

  return (
    <div className="flex flex-col gap-2 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Label className="text-sm font-semibold text-[#3F3F3F] flex items-center gap-2">
            {field.label}
            {isSecret && <Lock className="w-3 h-3 text-gray-400" />}
            {isSet && <CheckCircle2 className="w-3.5 h-3.5 text-green-600" title="Défini" />}
          </Label>
          {field.help && <p className="text-[11px] text-gray-500 mt-0.5">{field.help}</p>}
          {isSet && !editing && (
            <p className="text-[11px] font-mono text-gray-400 mt-1">
              Valeur actuelle : {isSecret ? (show ? displayValue : "••••••••") : (displayValue || <em>(vide)</em>)}
              {isSecret && (
                <button onClick={() => setShow(!show)} className="ml-2 text-[#0069B4] hover:underline align-middle">
                  {show ? <EyeOff className="w-3 h-3 inline" /> : <Eye className="w-3 h-3 inline" />}
                </button>
              )}
              {value?.updated_at && <span className="ml-2 text-gray-400">• MAJ {new Date(value.updated_at).toLocaleDateString("fr-FR")}</span>}
            </p>
          )}
        </div>
      </div>
      {!editing ? (
        <Button onClick={() => setEditing(true)} variant="outline" className="rounded-none text-xs h-8 w-fit" data-testid={`edit-setting-${field.key}`}>
          {isSet ? "Modifier" : "Définir"}
        </Button>
      ) : (
        <div className="flex gap-2">
          {field.select ? (
            <Select value={localValue} onValueChange={setLocalValue}>
              <SelectTrigger className="rounded-none text-sm flex-1" data-testid={`setting-input-${field.key}`}>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {field.select.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={isSecret ? "password" : "text"}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              placeholder={field.placeholder}
              className="rounded-none text-sm flex-1"
              autoFocus
              data-testid={`setting-input-${field.key}`}
            />
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-9" data-testid={`save-setting-${field.key}`}>
            <Save className="w-3.5 h-3.5 mr-1" /> Sauvegarder
          </Button>
          <Button onClick={() => { setEditing(false); setLocalValue(""); }} variant="ghost" className="rounded-none text-xs h-9">Annuler</Button>
        </div>
      )}
    </div>
  );
}

export default function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const runMigrateFormatting = async () => {
    if (!window.confirm("Lancer la mise au format de tous les utilisateurs ?\n\n• Téléphones normalisés (06 12 34 56 78)\n• Noms de famille mis en MAJUSCULES\n\nIdempotent — peut être relancé sans risque.")) return;
    setMigrating(true);
    try {
      const r = await api.post("/admin/maintenance/migrate-formatting");
      const d = r.data || {};
      toast.success(`Mise au format OK — ${d.users_scanned || 0} utilisateurs · ${d.phones_updated || 0} tél · ${d.last_names_updated || 0} noms · ${d.names_updated || 0} affichages`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur pendant la mise au format");
    } finally {
      setMigrating(false);
    }
  };

  const runBackfillNationality = async () => {
    if (!window.confirm("Renseigner la nationalité des candidats existants à partir du pays d'émission de leur pièce d'identité ?\n\nIdempotent — seuls les candidats sans nationalité seront mis à jour.")) return;
    setMigrating(true);
    try {
      const r = await api.post("/admin/maintenance/backfill-nationality");
      const d = r.data || {};
      const samples = (d.unmapped_samples || []).slice(0, 3).map((s) => `${s.country} (×${s.count})`).join(", ");
      const extra = d.unmapped > 0 ? ` · ${d.unmapped} pays non mappés${samples ? " (ex : " + samples + ")" : ""}` : "";
      toast.success(`Nationalités OK — ${d.users_scanned || 0} scannés · ${d.updated || 0} mis à jour${extra}`, { duration: 7000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur pendant le backfill");
    } finally {
      setMigrating(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/admin/settings");
      setSettings(s.data || {});
    } catch { toast.error("Impossible de charger la configuration"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (key, value) => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { key, value });
      toast.success(`${key} mis à jour`);
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Chargement…</div>;

  return (
    <div data-testid="settings-tab" className="space-y-6 max-w-3xl">
      {/* Guide PDF download */}
      <div className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-guide-card">
        <div className="flex items-center gap-3 min-w-0">
          <BookOpen className="w-6 h-6 shrink-0" />
          <div>
            <p className="text-sm font-bold">Guide administrateur (PDF)</p>
            <p className="text-xs opacity-90">Mode opératoire complet du Backoffice — utilisation quotidienne, bonnes pratiques.</p>
          </div>
        </div>
        <a
          href="/MODE_OPERATOIRE_ADMIN.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white text-[#662483] px-4 py-2 text-xs font-bold hover:bg-[#F0E9F3] transition-colors shrink-0"
          data-testid="download-guide-btn"
        >
          Télécharger le PDF
        </a>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title} className="bg-white border border-gray-200 p-5">
          <h3 className="text-base font-bold text-[#3F3F3F] mb-1">{section.title}</h3>
          <p className="text-xs text-gray-500 mb-3">{section.description}</p>
          <div>
            {section.fields.map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                value={settings[field.key]}
                onSave={handleSave}
                saving={saving}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Maintenance — one-shot data migrations */}
      <div className="bg-white border border-gray-200 p-5" data-testid="maintenance-section">
        <h3 className="text-base font-bold text-[#3F3F3F] mb-1 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#662483]" /> Maintenance des données
        </h3>
        <p className="text-xs text-gray-500 mb-4">Outils de mise au format pour aligner les données existantes sur les conventions actuelles (idempotents).</p>
        <div className="flex items-start justify-between gap-4 flex-wrap border border-gray-100 p-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#3F3F3F]">Reformatage téléphones + noms</p>
            <p className="text-xs text-gray-500 mt-1">Normalise tous les téléphones au format <code className="bg-gray-100 px-1">06 12 34 56 78</code> et passe les noms de famille en MAJUSCULES.</p>
          </div>
          <Button
            onClick={runMigrateFormatting}
            disabled={migrating}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-9"
            data-testid="run-migrate-formatting-btn"
          >
            {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
            {migrating ? "Traitement…" : "Lancer la mise au format"}
          </Button>
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap border border-gray-100 p-3 mt-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#3F3F3F]">Backfill nationalités</p>
            <p className="text-xs text-gray-500 mt-1">Déduit la nationalité (Française, Marocaine, …) des candidats existants sans valeur, à partir du pays d'émission de leur pièce d'identité (<code className="bg-gray-100 px-1">id_doc_origin</code>).</p>
          </div>
          <Button
            onClick={runBackfillNationality}
            disabled={migrating}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-9"
            data-testid="run-backfill-nationality-btn"
          >
            {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />}
            {migrating ? "Traitement…" : "Migrer les nationalités"}
          </Button>
        </div>
      </div>

      {/* Anael TT — Export manuel (avant ouverture du webservice) */}
      <div className="bg-white border border-gray-200 p-5" data-testid="anael-export-section">
        <h3 className="text-base font-bold text-[#3F3F3F] mb-1 flex items-center gap-2">
          <Download className="w-4 h-4 text-[#0069B4]" /> Export Anael TT (Infor)
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Tant que le webservice Anael n'est pas branché, vous pouvez générer un fichier JSON
          conforme à la doc Anael TT V9R4 et le transmettre manuellement à Infor.
          Tous les codes pays sont en <strong>ISO 3166-1 alpha-3</strong> (FRA, DZA, MAR…).
        </p>
        <div className="flex items-start justify-between gap-4 flex-wrap border border-gray-100 p-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#3F3F3F]">Salariés synchronisables (actifs)</p>
            <p className="text-xs text-gray-500 mt-1">Génère un fichier <code className="bg-gray-100 px-1">.json</code> avec le payload PER de chaque salarié actif et la liste des champs obligatoires manquants à compléter.</p>
          </div>
          <a
            href={`${API_BASE_URL}/api/admin/anael/export-json?scope=syncable`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-[#0069B4] to-[#662483] text-white rounded-none text-xs h-9 px-4 inline-flex items-center justify-center font-semibold hover:opacity-90"
            data-testid="anael-export-json-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Télécharger le JSON
          </a>
        </div>
      </div>
    </div>
  );
}
